// Documents → Files: the client's Drive folder, read live, one collapsible
// row per folder, and inside it a collapsible row for every folder it holds,
// at any depth (a submission's folder inside PA Submissions, a "Change
// Request" folder inside that) — empty ones too — so it shows everything on
// file the way it is organised, including documents that never passed through
// CAERA. Who uploaded or generated a file shows on
// the file itself (from CAERA's `documents` table); it is never listed apart.
// Nothing to do here except find a file or add one.
import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import OpenLink from '../OpenLink';
import { driveFolderUrl } from '../../lib/driveRoots';
import { flattenBucket, filesUnder, fileKind, fmtSize } from '../../lib/driveListing';

const fmtDay = (iso) => (iso ? format(new Date(iso), 'MMM d, yyyy') : '');

// Drive file id out of a webViewLink / Docs URL, so a `documents` row can be
// matched to the live listing without relying on the file name.
const driveId = (url) => (String(url || '').match(/\/d\/([\w-]+)/) || [])[1] || null;

// Fax pipeline file names carry the direction: "G005 – 2026-09-10 – SENT – …".
const faxDirection = (name) => (/ – SENT – /.test(name) ? 'Sent' : 'Received');

const st = {
  section: { border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--card-bg)', marginBottom: 14 },
  sectionHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 16px', background: 'var(--accent-bg)', borderBottom: '1px solid var(--accent-border)', flexWrap: 'wrap' },
  sectionTitle: { margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--accent)' },
  hint: { fontSize: 12, color: 'var(--text3)' },
  filed: { padding: '9px 16px', fontSize: 12.5, color: 'var(--text2)', background: 'var(--bg2)', display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
  kind: { fontSize: 10.5, fontWeight: 600, padding: '2px 0', borderRadius: 4, background: 'var(--card-bg)', color: 'var(--text2)', border: '1px solid var(--border)', textAlign: 'center', width: 44, flexShrink: 0 },
  linkBtn: { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' },
};

export default function ClientDocumentsTab({ client, driveTree, driveLoading, driveErr, onRefresh, documents = [], latestAssessment, onUpload }) {
  const [openFolders, setOpenFolders] = useState(() => new Set());
  const toggle = (id) => setOpenFolders((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Attribution: documents rows keyed by Drive file id.
  const byDriveId = useMemo(() => {
    const m = new Map();
    documents.forEach((d) => { const k = driveId(d.drive_url); if (k && !m.has(k)) m.set(k, d); });
    return m;
  }, [documents]);

  const folders = [...(driveTree?.folders || [])].sort((a, b) => (a.name === 'PA Submissions' ? -1 : b.name === 'PA Submissions' ? 1 : a.name.localeCompare(b.name)));
  const totalFiles = folders.reduce((n, f) => n + flattenBucket(f).filter((x) => !x.mimeType?.includes('folder')).length, 0) + (driveTree?.files?.length || 0);
  const [showLoose, setShowLoose] = useState(false);

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{client.first_name}'s files</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            The Drive folder, read live · 6 – Client Folder / {client.client_id}{driveTree?.folderId ? ` · ${totalFiles} file${totalFiles === 1 ? '' : 's'}` : ''} ·{' '}
            <button type="button" style={st.linkBtn} onClick={onRefresh} disabled={driveLoading}>{driveLoading ? 'Reading…' : 'Refresh'}</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={() => onUpload(null)}>Upload</button>
          {client.drive_folder_id && <OpenLink href={driveFolderUrl(client.drive_folder_id)}>Open in Drive</OpenLink>}
        </div>
      </div>

      <div style={{ ...st.section, marginBottom: 0 }}>

        {driveErr && <div style={{ color: 'var(--danger)', fontSize: 12.5, padding: '12px 16px' }}>{driveErr}</div>}
        {!driveErr && driveLoading && !driveTree && <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 16px' }}>Reading the Drive folder…</div>}
        {!driveErr && driveTree && !driveTree.folderId && (
          <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 16px' }}>No Drive folder could be found or created for this client — try Refresh, and if it persists the Drive connection needs looking at.</div>
        )}

        {driveTree?.folderId && folders.length === 0 && !driveTree.files?.length && (
          <div style={{ fontSize: 13, color: 'var(--text3)', padding: '12px 16px' }}>The folder is empty. Subfolders appear as documents are filed — or upload the first one now.</div>
        )}
        {folders.map((folder) => (
          <FolderRow key={folder.id} folder={folder} open={openFolders.has(folder.id)} onToggle={() => toggle(folder.id)}
            byDriveId={byDriveId} onUpload={onUpload}
            note={folderNote(folder.name, latestAssessment)} />
        ))}

        {driveTree?.files?.length > 0 && (
          <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 16px', fontSize: 12.5, color: 'var(--warn)', background: 'var(--warn-bg)' }}>
              <span>⚠</span>
              <span style={{ flex: 1 }}>{driveTree.files.length} file{driveTree.files.length === 1 ? ' sits' : 's sit'} loose in the client folder, not in any subfolder.</span>
              <button type="button" style={{ ...st.linkBtn, color: 'var(--warn)' }} onClick={() => setShowLoose((v) => !v)}>{showLoose ? 'Hide' : 'Show them'}</button>
            </div>
            {showLoose && driveTree.files.map((f) => <FileRow key={f.id} file={f} byDriveId={byDriveId} />)}
          </div>
        )}
      </div>

      <div style={{ fontSize: 11.5, color: 'var(--text3)' }}>
        Clinical &amp; progress notes and visit reports are kept in <b style={{ color: 'var(--text2)' }}>AxisCare</b> (Care Notes), signed and dated by care staff — Rule 111-8-65-.09(4)(a)(4).
      </div>
    </div>
  );
}

// A line under the file list that says where a folder's contents come from.
function folderNote(name, latestAssessment) {
  switch (name) {
    case 'Assessments':
      return latestAssessment
        ? <>Written in the <b>Clinical</b> tab; latest assessment {fmtDay(latestAssessment.updated_at)} — <OpenLink href={latestAssessment.drive_document_url} className="btn-ghost" style={{ padding: '0 4px' }}>View</OpenLink></>
        : <>Assessments are written in the <b>Clinical</b> tab; the finished documents land here.</>;
    case 'Faxes': return <>Received faxes are filed here automatically. Sent letters are in <b>Correspondence</b>.</>;
    case 'Signing Packets': return <>Older signed packets (SignWell era). New requests file under <b>PA Submissions</b>.</>;
    case 'PA Submissions': return <>One folder per submission to DCH — the signed forms, the supporting notes, the certificate, the LON.</>;
    case 'Supervisor Visits': return <>Written under <b>Clinical → Supervision</b>.</>;
    default: return null;
  }
}

// ── one folder, collapsed by default ─────────────────────────────────────────
const byNewest = (a, b) => String(b.modifiedTime).localeCompare(String(a.modifiedTime));
const count = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const Chevron = ({ open }) => <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--text3)" strokeWidth="2" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }}><path d="M6 3l5 5-5 5" /></svg>;
// What a folder holds, said once for a top-level row and a nested one alike.
const summary = (folder) => {
  const files = filesUnder(folder);
  const subs = (folder.subfolders || []).length + (folder.folderCount || 0);
  return { files, subs, latest: [...files].sort(byNewest)[0]?.modifiedTime, empty: !files.length && !subs };
};
const summaryText = ({ files, subs, empty }) => (empty ? 'empty' : [files.length ? count(files.length, 'file') : null, subs ? count(subs, 'folder') : null].filter(Boolean).join(' · '));

function FolderRow({ folder, open, onToggle, byDriveId, onUpload, note }) {
  const sum = summary(folder);
  const inFaxes = folder.name === 'Faxes';
  return (
    <div id={`folder-${folder.id}`}>
      <div role="button" tabIndex={0} aria-expanded={open}
        onClick={onToggle} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        style={{ display: 'grid', gridTemplateColumns: '18px 1fr auto', gap: 10, alignItems: 'center', padding: '10px 16px', borderTop: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' }}
        className="doc-folder-row">
        <Chevron open={open} />
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', minWidth: 0 }}>
          <span style={{ fontSize: 13.5, fontWeight: sum.empty ? 500 : 600, color: sum.empty ? 'var(--text3)' : 'var(--text)' }}>{folder.name}</span>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>{summaryText(sum)}</span>
          {sum.latest && <span style={{ fontSize: 12, color: 'var(--text3)' }}>latest {fmtDay(sum.latest)}</span>}
        </div>
        <span onClick={(e) => e.stopPropagation()}><OpenLink href={folder.webViewLink} className="btn-ghost">Open folder</OpenLink></span>
      </div>
      {open && (
        <div style={{ background: 'var(--bg2)', borderTop: '1px solid var(--border)' }}>
          {[...(folder.subfolders || [])].sort((a, b) => a.name.localeCompare(b.name)).map((sf) => (
            <SubFolder key={sf.id} folder={sf} depth={1} path={`${folder.name}/${sf.name}`} byDriveId={byDriveId} inFaxes={inFaxes} onUpload={onUpload} />
          ))}
          {[...(folder.files || [])].sort(byNewest).map((f) => <FileRow key={f.id} file={f} byDriveId={byDriveId} inFaxes={inFaxes} />)}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '8px 16px 8px 44px', fontSize: 12, color: 'var(--text3)', flexWrap: 'wrap' }}>
            <span>{sum.empty && !note ? 'Nothing here yet.' : note}</span>
            <button type="button" style={st.linkBtn} onClick={() => onUpload(folder.name)}>Upload to {folder.name}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// A folder inside a folder, collapsed by default and indented by its depth.
// path is where an upload from here lands ("PA Submissions/Transfer in · Sep 2026").
function SubFolder({ folder, depth, path, byDriveId, inFaxes, onUpload }) {
  const [open, setOpen] = useState(false);
  const sum = summary(folder);
  const indent = 44 + (depth - 1) * 22;
  return (
    <div>
      <div role="button" tabIndex={0} aria-expanded={open}
        onClick={() => setOpen((o) => !o)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        style={{ display: 'flex', gap: 10, alignItems: 'center', padding: `8px 16px 8px ${indent - 26}px`, borderTop: '1px solid var(--border)', cursor: 'pointer', userSelect: 'none' }}
        className="doc-folder-row">
        <Chevron open={open} />
        <span style={st.kind}>Folder</span>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: sum.empty ? 'var(--text3)' : 'var(--text)' }}>{folder.name}</span>
          <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>{summaryText(sum)}{sum.latest ? ` · latest ${fmtDay(sum.latest)}` : ''}</span>
        </div>
        <span onClick={(e) => e.stopPropagation()}><OpenLink href={folder.webViewLink} className="btn-ghost" style={{ flexShrink: 0 }}>Open</OpenLink></span>
      </div>
      {open && (
        <>
          {[...(folder.subfolders || [])].sort((a, b) => a.name.localeCompare(b.name)).map((sf) => (
            <SubFolder key={sf.id} folder={sf} depth={depth + 1} path={`${path}/${sf.name}`} byDriveId={byDriveId} inFaxes={inFaxes} onUpload={onUpload} />
          ))}
          {[...(folder.files || [])].sort(byNewest).map((f) => <FileRow key={f.id} file={f} byDriveId={byDriveId} inFaxes={inFaxes} indent={indent + 22} />)}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: `6px 16px 6px ${indent + 22}px`, fontSize: 12, color: 'var(--text3)', borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <span>{folder.folderCount ? `${count(folder.folderCount, 'more folder')} inside — open it in Drive to see them.` : sum.empty ? 'Nothing in this folder yet.' : ''}</span>
            <button type="button" style={st.linkBtn} onClick={() => onUpload(path)}>Upload to {folder.name}</button>
          </div>
        </>
      )}
    </div>
  );
}

function FileRow({ file: f, byDriveId, inFaxes, indent = 44 }) {
  const doc = byDriveId.get(f.id);
  const isFolder = f.mimeType?.includes('folder');
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: `7px 16px 7px ${indent}px`, borderTop: '1px solid var(--border)' }}>
      <span style={st.kind}>{fileKind(f.mimeType, f.name)}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', gap: 8, alignItems: 'center' }}>
          {inFaxes && !isFolder && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '0 5px', borderRadius: 4, flexShrink: 0, background: faxDirection(f.name) === 'Sent' ? 'var(--bg2)' : 'var(--accent-bg)', color: faxDirection(f.name) === 'Sent' ? 'var(--text2)' : 'var(--accent)', border: '1px solid var(--border)' }}>{faxDirection(f.name)}</span>
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {f.path && <span style={{ color: 'var(--accent)' }}>{f.path} ·</span>}
          {f.modifiedTime && <span>{fmtDay(f.modifiedTime)}</span>}
          {f.size && <span>· {fmtSize(f.size)}</span>}
          {doc?.uploaded_by_name && <span style={{ color: 'var(--text2)' }}>· {doc.document_type === 'Signing Packets' || /assessment/i.test(doc.notes || '') ? 'Generated' : 'Uploaded'} by {doc.uploaded_by_name}</span>}
          {doc?.notes && <span style={{ color: 'var(--text2)', fontStyle: 'italic' }}>· {doc.notes}</span>}
        </div>
      </div>
      <OpenLink href={f.webViewLink} className="btn-ghost" style={{ flexShrink: 0 }}>Open</OpenLink>
    </div>
  );
}
